/**
 * Assistant API error unwrapping.
 *
 * The server returns meaningful bodies on failures — 400 (already handled),
 * 413 (file too large), 403 (role gate) — but an axios rejection's `.message`
 * is only the generic "Request failed with status code NNN". Views render
 * `error.message` into role="alert", so the service layer is the boundary that
 * translates transport errors into the server's domain message. Anything
 * mocking a service function must honor this contract (reject with
 * `unwrapApiError(axiosShapedError)`).
 *
 * DRF emits three shapes, all handled here:
 *   {"detail": "..."}            APIException (403/404/429/…)
 *   ["..."]                      ValidationError raised with a bare string
 *   {"field": ["...", …]}        ValidationError raised with a field map
 */
/** A string, or the first string in an array. Nothing deeper — a nested object
 *  is machine detail (field locations, codes), never a message for a human. */
function asMessage(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return '';
}

function firstMessage(body: unknown): string {
  if (!body || typeof body !== 'object') return asMessage(body);
  if (Array.isArray(body)) return asMessage(body);
  const record = body as Record<string, unknown>;
  // `detail` wins when present; otherwise take the first field's message.
  return asMessage(record.detail) || asMessage(Object.values(record)[0]);
}

export function unwrapApiError(error: unknown): Error {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  const message = firstMessage(data);
  if (message) {
    const unwrapped = new Error(message);
    // tsconfig lib is ES2015 (no ErrorOptions typing) — attach imperatively.
    (unwrapped as Error & { cause?: unknown }).cause = error;
    return unwrapped;
  }
  return error instanceof Error ? error : new Error(String(error));
}

/** Await `promise`, rethrowing any rejection through {@link unwrapApiError}. */
export async function withApiErrors<T>(promise: Promise<T>): Promise<T> {
  try {
    return await promise;
  } catch (error) {
    throw unwrapApiError(error);
  }
}
